import { useState, useMemo, useEffect, useCallback, useLayoutEffect, useRef } from 'react'
import {
  Plus, Search, Eye, Trash2, Camera, FileText, Shield, X,
  Globe, Lock, Pause, List as ListIcon, LayoutGrid,
} from 'lucide-react'
import { useInfinitePersons } from '@/hooks/useInfinitePersons'
import { usePersons } from '@/hooks/usePersons'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { useThrottle } from '@/hooks/useThrottle'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { personApi, PERSON_STATUSES, PROFILE_TYPES, type PersonStatus, type ProfileType, type ProfileTypeFilter } from '@/services/api/persons'
import { tagApi } from '@/services/api/tags'
import { formatRelative } from '@/lib/utils'
import toast from 'react-hot-toast'
import { useAuthorities, AuthorityConstants } from '@/hooks/useAuthorities'
import { HasAuthority } from '@/components/auth/AuthorityGuard'
import { PersonThumbnail } from '@/components/PersonThumbnail'
import { TagPicker } from '@/components/TagPicker'

const STATUS_FILTER_OPTIONS: PersonStatus[] = [...PERSON_STATUSES]
const PROFILE_TYPE_FILTER_OPTIONS: ProfileTypeFilter[] = [...PROFILE_TYPES, 'Unassigned']

// Deterministic avatar gradient bucket (0..5) from a name hash.
// Matches the .bk-avatar-c0..c5 classes defined in bk-components.css.
function avatarColorClass(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return `bk-avatar-c${Math.abs(hash) % 6}`
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]!.toUpperCase()).join('') || '?'
}

function StatusPill({ status }: { status: string }) {
  if (status === 'Public') {
    return <span className="bk-badge bk-badge-public bk-badge-dot"><Globe size={11} /> Public</span>
  }
  if (status === 'Inactive') {
    return <span className="bk-badge bk-badge-soft bk-badge-dot"><Pause size={11} /> Inactive</span>
  }
  return <span className="bk-badge bk-badge-private bk-badge-dot"><Lock size={11} /> Private</span>
}

function ProfileTypeBadge({ profileType }: { profileType?: string }) {
  if (!profileType) return null
  const tone = profileType === 'BK' ? 'bk-badge-bk' : 'bk-badge-guest'
  return <span className={`bk-badge ${tone}`}>{profileType}</span>
}

function TagChips({ tags, max = 3 }: { tags?: string[]; max?: number }) {
  if (!tags || tags.length === 0) return null
  const shown = tags.slice(0, max)
  const overflow = tags.length - shown.length
  return (
    <div className="bk-tag-row">
      {shown.map(t => <span key={t} className="bk-tag-chip">{t}</span>)}
      {overflow > 0 && <span className="bk-tag-overflow">+{overflow}</span>}
    </div>
  )
}

export default function PersonsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'last_activity')
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc')
  const [statusFilter, setStatusFilter] = useState<PersonStatus | ''>(
    (searchParams.get('status') as PersonStatus) || ''
  )
  const [profileTypeFilter, setProfileTypeFilter] = useState<ProfileTypeFilter | ''>(
    (searchParams.get('profileType') as ProfileTypeFilter) || ''
  )

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  // Gmail-style "select everything matching the filter" mode. When true the
  // effective selection is *all* matching rows (matchingIds), not just what's
  // in selectedIds. Toggling any individual row exits this mode.
  const [selectAllMatching, setSelectAllMatching] = useState(false)
  const [matchingIds, setMatchingIds] = useState<number[]>([])
  const [bulkStatus, setBulkStatus] = useState<PersonStatus>('Public')
  const [bulkProfileType, setBulkProfileType] = useState<ProfileType | ''>('')
  const [bulkTags, setBulkTags] = useState<string[]>([])
  const [viewMode, setViewMode] = useState<'list' | 'cards'>(() =>
    (typeof window !== 'undefined' && (localStorage.getItem('personsView') as 'list' | 'cards')) || 'list')

  useEffect(() => { localStorage.setItem('personsView', viewMode) }, [viewMode])

  const { canDeletePerson, canEditPerson, canSeeAllPersons, canSeeInactivePersons } = useAuthorities()
  const canSeeAll = canSeeAllPersons()
  const canSeeInactive = canSeeInactivePersons()

  const updateSearchParams = useCallback((updates: { search?: string; sortBy?: string; sortOrder?: string; status?: string; profileType?: string }) => {
    const newParams = new URLSearchParams(searchParams)
    if (updates.search !== undefined) {
      if (updates.search) newParams.set('search', updates.search)
      else newParams.delete('search')
    }
    if (updates.sortBy !== undefined) newParams.set('sortBy', updates.sortBy)
    if (updates.sortOrder !== undefined) newParams.set('sortOrder', updates.sortOrder)
    if (updates.status !== undefined) {
      if (updates.status) newParams.set('status', updates.status)
      else newParams.delete('status')
    }
    if (updates.profileType !== undefined) {
      if (updates.profileType) newParams.set('profileType', updates.profileType)
      else newParams.delete('profileType')
    }
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    updateSearchParams({ search: searchTerm, sortBy, sortOrder, status: statusFilter, profileType: profileTypeFilter })
  }, [searchTerm, sortBy, sortOrder, statusFilter, profileTypeFilter, updateSearchParams])

  // Reset selection whenever filters change so selections don't apply to hidden rows.
  useEffect(() => {
    setSelectedIds(new Set())
    setSelectAllMatching(false)
    setMatchingIds([])
  }, [searchTerm, statusFilter, profileTypeFilter, sortBy, sortOrder])

  const params = useMemo(() => ({
    search: searchTerm || undefined,
    status: statusFilter || undefined,
    profile_type: profileTypeFilter || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  }), [searchTerm, statusFilter, profileTypeFilter, sortBy, sortOrder])

  const fallbackParams = useMemo(() => ({
    page: 1, size: 20,
    search: searchTerm || undefined,
    status: statusFilter || undefined,
    profile_type: profileTypeFilter || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  }), [searchTerm, statusFilter, profileTypeFilter, sortBy, sortOrder])

  const fallbackQuery = usePersons(fallbackParams)

  const { data: personStats } = useQuery({
    queryKey: ['persons', 'stats'],
    queryFn: () => personApi.getStats(),
    staleTime: 60_000,
  })

  const { data: tagOptions = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: tagApi.list,
  })

  const {
    data, isLoading, isError, error,
    fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfinitePersons(params)

  const shouldUseFallback = isError && !fallbackQuery.isError && (!data?.pages || data.pages.length === 0)
  const finalIsLoading = shouldUseFallback ? fallbackQuery.isLoading : isLoading
  const finalIsError   = shouldUseFallback ? fallbackQuery.isError   : isError
  const finalError     = shouldUseFallback ? fallbackQuery.error     : error

  const persons = useMemo(() => {
    if (shouldUseFallback) return fallbackQuery.data?.items || []
    if (!data?.pages) return []
    return data.pages.flatMap(page => page.items || [])
  }, [data, fallbackQuery.data, shouldUseFallback])

  const totalPersons = useMemo(() => {
    if (shouldUseFallback) return fallbackQuery.data?.total || 0
    if (!data?.pages?.[0]) return 0
    return data.pages[0].total || 0
  }, [data, fallbackQuery.data, shouldUseFallback])

  const { targetRef, isIntersecting } = useIntersectionObserver({ threshold: 0.1, rootMargin: '100px' })

  // Scroll-position preservation across page loads (see prior implementation note).
  const savedScrollYRef = useRef<number | null>(null)
  const lastPageCountRef = useRef(0)
  useLayoutEffect(() => {
    const newCount = data?.pages?.length ?? 0
    if (newCount > lastPageCountRef.current && savedScrollYRef.current !== null) {
      const target = savedScrollYRef.current
      savedScrollYRef.current = null
      window.scrollTo(0, target)
    }
    lastPageCountRef.current = newCount
  }, [data?.pages?.length])

  const fetchNextPageThrottled = useThrottle(
    useCallback(() => {
      if (!shouldUseFallback && hasNextPage && !isFetchingNextPage && !finalIsLoading) {
        savedScrollYRef.current = window.scrollY
        fetchNextPage()
      }
    }, [shouldUseFallback, hasNextPage, isFetchingNextPage, finalIsLoading, fetchNextPage]),
    2000,
  )

  useEffect(() => {
    if (isIntersecting) fetchNextPageThrottled()
  }, [isIntersecting, fetchNextPageThrottled])

  const handleAddPerson = () => navigate('/persons/new')

  const deleteMutation = useMutation({
    mutationFn: personApi.deletePerson,
    onSuccess: () => {
      toast.success('Person deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['persons'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      queryClient.invalidateQueries({ queryKey: ['infinite-persons'] })
    },
    onError: (err: any) => {
      console.error('Delete person error:', err)
      toast.error(err?.response?.data?.message || 'Failed to delete person')
    },
  })

  const handleDeletePerson = (personId: number, personName: string) => {
    if (window.confirm(`Are you sure you want to delete ${personName}? This action cannot be undone.`)) {
      deleteMutation.mutate(personId)
    }
  }

  const bulkStatusMutation = useMutation({
    mutationFn: ({ ids, status }: { ids: number[]; status: PersonStatus }) =>
      personApi.bulkUpdateStatus(ids, status),
    onSuccess: (result) => {
      toast.success(`Updated ${result.updated} person${result.updated === 1 ? '' : 's'} to ${result.status}`)
      clearSelection()
      queryClient.invalidateQueries({ queryKey: ['persons'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to update status'
      toast.error(msg)
    },
  })

  const bulkProfileTypeMutation = useMutation({
    mutationFn: ({ ids, profileType }: { ids: number[]; profileType: ProfileType | '' }) =>
      personApi.bulkUpdateProfileType(ids, profileType),
    onSuccess: (result) => {
      toast.success(`Updated ${result.updated} profile${result.updated === 1 ? '' : 's'}`)
      clearSelection()
      queryClient.invalidateQueries({ queryKey: ['persons'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to update profile type'
      toast.error(msg)
    },
  })

  const bulkTagsMutation = useMutation({
    mutationFn: ({ ids, tags }: { ids: number[]; tags: string[] }) =>
      personApi.bulkSetTags(ids, tags, 'add'),
    onSuccess: (result) => {
      toast.success(`Updated tags on ${result.updated} profile${result.updated === 1 ? '' : 's'}`)
      clearSelection()
      setBulkTags([])
      queryClient.invalidateQueries({ queryKey: ['persons'] })
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to update tags'
      toast.error(msg)
    },
  })

  const visibleIds = useMemo(() => persons.map((p: any) => p.id as number), [persons])
  const selectedCount = selectAllMatching ? totalPersons : selectedIds.size

  const clearSelection = () => {
    setSelectedIds(new Set())
    setSelectAllMatching(false)
    setMatchingIds([])
  }

  const handleApplyBulkStatus = () => {
    const ids = selectAllMatching ? matchingIds : Array.from(selectedIds)
    if (ids.length === 0) return
    if (!window.confirm(`Change status of ${ids.length} person${ids.length === 1 ? '' : 's'} to "${bulkStatus}"?`)) return
    bulkStatusMutation.mutate({ ids, status: bulkStatus })
  }

  const handleApplyBulkProfileType = () => {
    const ids = selectAllMatching ? matchingIds : Array.from(selectedIds)
    if (ids.length === 0) return
    const label = bulkProfileType || '(none)'
    if (!window.confirm(`Set profile type of ${ids.length} person${ids.length === 1 ? '' : 's'} to "${label}"?`)) return
    bulkProfileTypeMutation.mutate({ ids, profileType: bulkProfileType })
  }

  const handleApplyBulkTags = () => {
    const ids = selectAllMatching ? matchingIds : Array.from(selectedIds)
    if (ids.length === 0) return
    if (bulkTags.length === 0) return
    if (!window.confirm(`Add tags to ${ids.length} person${ids.length === 1 ? '' : 's'}: ${bulkTags.join(', ')}?`)) return
    bulkTagsMutation.mutate({ ids, tags: bulkTags })
  }

  const toggleRow = (id: number) => {
    // Toggling any single row drops out of "all matching" mode, falling back to
    // an explicit selection of just the currently-loaded rows (Gmail behavior).
    if (selectAllMatching) {
      const next = new Set(visibleIds)
      next.delete(id)
      setSelectAllMatching(false)
      setMatchingIds([])
      setSelectedIds(next)
      return
    }
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const allVisibleSelected = selectAllMatching || (visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id)))
  const someVisibleSelected = !allVisibleSelected && visibleIds.some(id => selectedIds.has(id))
  const isRowSelected = (id: number) => selectAllMatching || selectedIds.has(id)

  const toggleAllVisible = () => {
    if (selectAllMatching) { clearSelection(); return }
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (allVisibleSelected) visibleIds.forEach(id => next.delete(id))
      else visibleIds.forEach(id => next.add(id))
      return next
    })
  }

  const [selectingAll, setSelectingAll] = useState(false)
  const enableSelectAllMatching = async () => {
    setSelectingAll(true)
    try {
      const ids = await personApi.getMatchingIds({
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        profile_type: profileTypeFilter || undefined,
      })
      setMatchingIds(ids)
      setSelectAllMatching(true)
    } catch {
      toast.error('Failed to select all matching persons')
    } finally {
      setSelectingAll(false)
    }
  }

  // Callback ref to drive the tri-state (indeterminate) select-all checkbox.
  const setSelectAllRef = useCallback((el: HTMLInputElement | null) => {
    if (el) el.indeterminate = someVisibleSelected
  }, [someVisibleSelected])

  // Filter status segmented control — restricted by RBAC.
  // Non-admins: Public only. ROLE_USER_ALL: Private + Public. Inactive is
  // limited to the inactive-capable admin tier.
  const visibleStatusOptions = STATUS_FILTER_OPTIONS.filter(s =>
    s === 'Public' || (canSeeAll && (s !== 'Inactive' || canSeeInactive)))

  return (
    <div style={{ background: 'var(--bk-bg)', minHeight: '100%' }}>
      {/* Page hero */}
      <section className="bk-page-hero">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1 className="bk-page-title">Persons</h1>
            <p className="bk-page-sub">
              Manage profiles, photos, biographies and media release forms for the BK community.
            </p>

            <div className="bk-stat-row" style={{ marginTop: 18 }}>
              <button
                type="button"
                className="bk-stat"
                onClick={() => setStatusFilter('')}
                title="Show all"
              >
                <span className="dot" />
                <span>Total <span className="num">{personStats?.total ?? totalPersons}</span></span>
              </button>
              <button
                type="button"
                className="bk-stat bk-stat-public"
                onClick={() => setStatusFilter('Public')}
                title="Filter Public"
              >
                <span className="dot" />
                <span>Public <span className="num">{personStats?.by_status?.Public ?? '…'}</span></span>
              </button>
              {canSeeAll && (
                <>
                  <button
                    type="button"
                    className="bk-stat bk-stat-private"
                    onClick={() => setStatusFilter('Private')}
                    title="Filter Private"
                  >
                    <span className="dot" />
                    <span>Private <span className="num">{personStats?.by_status?.Private ?? '…'}</span></span>
                  </button>
                  {canSeeInactive && (
                    <button
                      type="button"
                      className="bk-stat bk-stat-pending"
                      onClick={() => setStatusFilter('Inactive')}
                      title="Filter Inactive"
                    >
                      <span className="dot" />
                      <span>Inactive <span className="num">{personStats?.by_status?.Inactive ?? '…'}</span></span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <HasAuthority authorities={[AuthorityConstants.ADMIN, AuthorityConstants.USER_ADMIN, AuthorityConstants.USER_ADMIN_D]}>
            <button type="button" className="bk-btn bk-btn-primary bk-btn-lg" onClick={handleAddPerson}>
              <Plus size={16} />
              Add Person
            </button>
          </HasAuthority>
        </div>
      </section>

      {/* Toolbar */}
      <div style={{ padding: '20px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="bk-toolbar">
          <div className="bk-input-wrap has-icon has-suffix" style={{ flex: '1 1 240px', minWidth: 220, maxWidth: 480 }}>
            <span className="bk-input-icon"><Search size={16} /></span>
            <input
              type="text"
              className="bk-input"
              placeholder="Search by name, email, or any field…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="bk-input-clear"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div style={{ flex: 1 }} />

          {/* Sort + view toggle */}
          <select
            className="bk-select"
            value={`${sortBy}-${sortOrder}`}
            onChange={e => {
              const [k, o] = e.target.value.split('-')
              setSortBy(k); setSortOrder(o)
            }}
            aria-label="Sort"
            style={{ height: 38, paddingRight: 32 }}
          >
            <option value="last_activity-desc">Recently modified</option>
            <option value="name-asc">Name A → Z</option>
            <option value="name-desc">Name Z → A</option>
            <option value="status-asc">Status A → Z</option>
          </select>

          <div className="bk-seg" role="tablist" aria-label="View mode">
            <button type="button" data-active={viewMode === 'list'} onClick={() => setViewMode('list')} aria-label="List view">
              <ListIcon size={14} />
            </button>
            <button type="button" data-active={viewMode === 'cards'} onClick={() => setViewMode('cards')} aria-label="Card view">
              <LayoutGrid size={14} />
            </button>
          </div>
        </div>

        <div className="bk-toolbar">
          {/* Status segmented filter */}
          <div className="bk-seg" role="tablist" aria-label="Filter by status">
            <button type="button" data-active={statusFilter === ''} onClick={() => setStatusFilter('')}>All</button>
            {visibleStatusOptions.map(s => (
              <button
                key={s}
                type="button"
                data-active={statusFilter === s}
                onClick={() => setStatusFilter(s)}
              >
                {s === 'Public'   && <Globe size={13} />}
                {s === 'Private'  && <Lock size={13} />}
                {s === 'Inactive' && <Pause size={13} />}
                {s}
              </button>
            ))}
          </div>

          {/* Profile type segmented filter */}
          <div className="bk-seg" role="tablist" aria-label="Filter by profile type">
            <button type="button" data-active={profileTypeFilter === ''} onClick={() => setProfileTypeFilter('')}>
              All{typeof personStats?.total === 'number' ? ` (${personStats.total})` : ''}
            </button>
            {PROFILE_TYPE_FILTER_OPTIONS.map(pt => (
              <button
                key={pt}
                type="button"
                data-active={profileTypeFilter === pt}
                onClick={() => setProfileTypeFilter(pt)}
              >
                {pt}{typeof personStats?.by_profile_type?.[pt] === 'number' ? ` (${personStats.by_profile_type[pt]})` : ''}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {canEditPerson() && selectedCount > 0 && (
        <div
          style={{
            margin: '0 32px 14px', padding: '10px 16px',
            background: 'var(--bk-accent-soft)', borderRadius: 'var(--bk-radius-md)',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--bk-accent-ink)' }}>
            {selectedCount} selected
          </span>

          {/* Gmail-style select-all / clear prompt */}
          {selectAllMatching ? (
            <span style={{ fontSize: 13, color: 'var(--bk-accent-ink)' }}>
              All {totalPersons} persons matching this filter are selected.{' '}
              <button
                type="button"
                onClick={clearSelection}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--bk-accent)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear selection
              </button>
            </span>
          ) : allVisibleSelected && totalPersons > visibleIds.length ? (
            <span style={{ fontSize: 13, color: 'var(--bk-accent-ink)' }}>
              All {visibleIds.length} on this page are selected.{' '}
              <button
                type="button"
                onClick={enableSelectAllMatching}
                disabled={selectingAll}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--bk-accent)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
              >
                {selectingAll ? 'Selecting…' : `Select all ${totalPersons} persons matching this filter`}
              </button>
            </span>
          ) : null}

          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 13, color: 'var(--bk-accent-ink)', whiteSpace: 'nowrap' }}>Set status to</span>
          <select
            className="bk-select"
            value={bulkStatus}
            onChange={e => setBulkStatus(e.target.value as PersonStatus)}
            style={{ height: 32, paddingRight: 28, fontSize: 13 }}
          >
            {PERSON_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            type="button"
            className="bk-btn bk-btn-primary bk-btn-sm"
            onClick={handleApplyBulkStatus}
            disabled={bulkStatusMutation.isPending}
          >
            {bulkStatusMutation.isPending ? 'Applying…' : 'Apply'}
          </button>
          <button type="button" className="bk-btn bk-btn-ghost bk-btn-sm" onClick={clearSelection}>
            <X size={14} /> Clear
          </button>
        </div>
      )}

      {canEditPerson() && selectedCount > 0 && (
        <div
          style={{
            margin: '0 32px 14px', padding: '10px 16px',
            background: 'var(--bk-bg-soft)', borderRadius: 'var(--bk-radius-md)',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 13, color: 'var(--bk-ink-2)', whiteSpace: 'nowrap' }}>Set profile type to</span>
          <select
            className="bk-select"
            value={bulkProfileType}
            onChange={e => setBulkProfileType(e.target.value as ProfileType | '')}
            style={{ height: 32, paddingRight: 28, fontSize: 13 }}
          >
            <option value="">— None —</option>
            {PROFILE_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
          </select>
          <button
            type="button"
            className="bk-btn bk-btn-secondary bk-btn-sm"
            onClick={handleApplyBulkProfileType}
            disabled={bulkProfileTypeMutation.isPending}
          >
            {bulkProfileTypeMutation.isPending ? 'Applying…' : 'Apply'}
          </button>

          <div style={{ width: 1, height: 24, background: 'var(--bk-border)' }} />

          <span style={{ fontSize: 13, color: 'var(--bk-ink-2)', whiteSpace: 'nowrap' }}>Add tags</span>
          <div style={{ minWidth: 220 }}>
            <TagPicker value={bulkTags} onChange={setBulkTags} options={tagOptions} placeholder="Tags to add…" />
          </div>
          <button
            type="button"
            className="bk-btn bk-btn-secondary bk-btn-sm"
            onClick={handleApplyBulkTags}
            disabled={bulkTagsMutation.isPending || bulkTags.length === 0}
          >
            {bulkTagsMutation.isPending ? 'Applying…' : 'Apply'}
          </button>
        </div>
      )}

      {/* Results card */}
      <div style={{ padding: '0 32px 64px' }}>
        <div className="bk-card">
          {/* Subhead bar */}
          <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--bk-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
            {canEditPerson() && persons.length > 0 && (
              <input
                ref={setSelectAllRef}
                type="checkbox"
                className="check"
                aria-label="Select all on this page"
                checked={allVisibleSelected}
                onChange={toggleAllVisible}
                style={{ position: 'relative', left: -18, marginRight: -10, cursor: 'pointer' }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="bk-display" style={{ fontSize: 16, fontWeight: 600 }}>
                {statusFilter ? `${statusFilter} persons` : 'All persons'}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--bk-muted)', marginTop: 2 }}>
                Showing {persons.length} of {totalPersons}{searchTerm ? ` · matching “${searchTerm}”` : ''}
              </div>
            </div>
            <span className="bk-badge bk-badge-outline">{totalPersons} total</span>
          </div>

          {finalIsLoading ? (
            <div style={{ padding: 24, display: 'grid', gap: 12 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bk-skel" style={{ height: 56 }} />
              ))}
            </div>
          ) : finalIsError ? (
            <div className="bk-empty">
              <div className="icon"><X size={22} /></div>
              <div className="ttl">Failed to load persons</div>
              <div className="sub">{(finalError as any)?.message || 'Something went wrong while fetching the list.'}</div>
              <button type="button" className="bk-btn bk-btn-secondary" onClick={() => window.location.reload()}>
                Try again
              </button>
            </div>
          ) : persons.length === 0 ? (
            <div className="bk-empty">
              <div className="icon"><Search size={22} /></div>
              <div className="ttl">No results</div>
              <div className="sub">
                {searchTerm || statusFilter || profileTypeFilter
                  ? 'Try adjusting your search terms or filters.'
                  : 'No persons in the system yet.'}
              </div>
              {(searchTerm || statusFilter || profileTypeFilter) ? (
                <button
                  type="button"
                  className="bk-btn bk-btn-secondary"
                  onClick={() => { setSearchTerm(''); setStatusFilter(''); setProfileTypeFilter('') }}
                >
                  Reset filters
                </button>
              ) : (
                <HasAuthority authorities={[AuthorityConstants.ADMIN, AuthorityConstants.USER_ADMIN, AuthorityConstants.USER_ADMIN_D]}>
                  <button type="button" className="bk-btn bk-btn-primary" onClick={handleAddPerson}>
                    <Plus size={14} /> Add your first person
                  </button>
                </HasAuthority>
              )}
            </div>
          ) : viewMode === 'cards' ? (
            <div className="bk-grid">
              {persons.map((p: any) => {
                const name = p.display_name || 'Unnamed Person'
                const isSelected = isRowSelected(p.id)
                return (
                  <div
                    key={p.id}
                    className="bk-pcard"
                    style={isSelected ? { borderColor: 'var(--bk-accent)' } : undefined}
                  >
                    {canEditPerson() && (
                      <input
                        type="checkbox"
                        className="check"
                        aria-label={`Select ${name}`}
                        checked={isSelected}
                        onChange={() => toggleRow(p.id)}
                        onClick={e => e.stopPropagation()}
                        style={{ position: 'absolute', top: 12, right: 12, cursor: 'pointer' }}
                      />
                    )}
                    <div className="top">
                      <div onClick={() => navigate(`/persons/${p.id}`)} style={{ cursor: 'pointer' }}>
                        {p.thumbnail_photo_url ? (
                          <PersonThumbnail photoUrl={p.thumbnail_photo_url} size={64} alt={name} />
                        ) : (
                          <img src="/images/contact-image.png" alt={name} style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover' }} />
                        )}
                      </div>
                      <div className="meta">
                        <div
                          className="name"
                          onClick={() => navigate(`/persons/${p.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          {name}
                        </div>
                        {p.email && <div className="role">{p.email}</div>}
                        <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <StatusPill status={p.status} />
                          <ProfileTypeBadge profileType={p.profile_type} />
                        </div>
                        {p.tags?.length > 0 && <div style={{ marginTop: 6 }}><TagChips tags={p.tags} /></div>}
                      </div>
                    </div>
                    <div className="stats">
                      <div className="stat" data-on={(p.photo_count || 0) > 0}>
                        <Camera size={13} /> {p.photo_count || 0}
                      </div>
                      <div className="stat" data-on={(p.biodata_count || 0) > 0}>
                        <FileText size={13} /> {p.biodata_count || 0}
                      </div>
                      <div className="stat" data-on={(p.media_release_count || 0) > 0}>
                        <Shield size={13} /> {p.media_release_count || 0}
                      </div>
                    </div>
                    <div className="footer">
                      {p.last_activity_date && <span>{formatRelative(p.last_activity_date)}</span>}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="bk-list">
              {persons.map((p: any) => {
                const name = p.display_name || 'Unnamed Person'
                const statusTone = p.status === 'Public' ? 'public' : p.status === 'Inactive' ? 'inactive' : 'private'
                return (
                  <div
                    key={p.id}
                    className="bk-list-row"
                  >
                    {canEditPerson() && (
                      <input
                        type="checkbox"
                        className="check"
                        aria-label={`Select ${name}`}
                        checked={isRowSelected(p.id)}
                        onChange={() => toggleRow(p.id)}
                        onClick={e => e.stopPropagation()}
                        style={{ position: 'absolute', marginLeft: -20, marginTop: 12, cursor: 'pointer' }}
                      />
                    )}

                    <div
                      className="photo"
                      aria-hidden="true"
                      onClick={() => navigate(`/persons/${p.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      {p.thumbnail_photo_url ? (
                        <PersonThumbnail photoUrl={p.thumbnail_photo_url} size={112} alt={name} />
                      ) : (
                        <img src="/images/contact-image.png" alt={name} style={{ width: 112, height: 112, borderRadius: 10, objectFit: 'cover' }} />
                      )}
                    </div>

                    <div className="body">
                      <h3
                        className="name"
                        onClick={() => navigate(`/persons/${p.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {name}
                      </h3>
                      <div className="meta">
                        {p.email && (
                          <>
                            <span className="role">{p.email}</span>
                            <span className="sep">·</span>
                          </>
                        )}
                        <span className="status" data-tone={statusTone}>{p.status}</span>
                        {p.profile_type && (
                          <>
                            <span className="sep">·</span>
                            <ProfileTypeBadge profileType={p.profile_type} />
                          </>
                        )}
                        {p.last_activity_date && (
                          <>
                            <span className="sep">·</span>
                            <span>edited {formatRelative(p.last_activity_date)}</span>
                          </>
                        )}
                      </div>
                      {p.tags?.length > 0 && <div style={{ marginTop: 6 }}><TagChips tags={p.tags} /></div>}
                    </div>

                    <div className="counts" aria-label="Upload counts">
                      <div className="count-chip" data-on={(p.photo_count || 0) > 0} title={`${p.photo_count || 0} photos`}>
                        <Camera size={14} />
                        <span>{p.photo_count || 0}</span>
                        <span className="lbl">Photos</span>
                      </div>
                      <div className="count-chip" data-on={(p.biodata_count || 0) > 0} title={`${p.biodata_count || 0} biodata entries`}>
                        <FileText size={14} />
                        <span>{p.biodata_count || 0}</span>
                        <span className="lbl">Bios</span>
                      </div>
                      <div className="count-chip" data-on={(p.media_release_count || 0) > 0} title={`${p.media_release_count || 0} media release forms`}>
                        <Shield size={14} />
                        <span>{p.media_release_count || 0}</span>
                        <span className="lbl">MRFs</span>
                      </div>
                    </div>

                    <div className="actions" onClick={e => e.stopPropagation()}>
                      <Link
                        to={`/persons/${p.id}`}
                        className="bk-btn bk-btn-secondary bk-btn-sm"
                        aria-label="View details"
                        title="View details"
                      >
                        <Eye size={14} /> View
                      </Link>
                      {canDeletePerson() && (
                        <button
                          type="button"
                          className="bk-btn bk-btn-danger bk-btn-sm"
                          onClick={() => handleDeletePerson(p.id, name)}
                          disabled={deleteMutation.isPending}
                          aria-label="Delete person"
                          title={deleteMutation.isPending ? 'Deleting…' : 'Delete person'}
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Infinite-scroll sentinel */}
          {!shouldUseFallback && hasNextPage && (
            <div
              ref={targetRef}
              style={{ overflowAnchor: 'none', padding: '20px', borderTop: '1px solid var(--bk-border)', textAlign: 'center', color: 'var(--bk-muted)', fontSize: 13 }}
            >
              {isFetchingNextPage ? 'Loading more persons…' : 'Scroll down to load more'}
            </div>
          )}
          {!shouldUseFallback && !hasNextPage && totalPersons > 0 && persons.length > 0 && (
            <div style={{ padding: '20px', borderTop: '1px solid var(--bk-border)', textAlign: 'center', fontSize: 13, color: 'var(--bk-success)' }}>
              All {totalPersons} persons loaded
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
